/**
 * Version Service for Dashboard
 * Handles version tracking, checking, and validation
 */

import { STORES } from './indexedDBService';

const DB_NAME = 'ShepherdDB';

/**
 * Get database connection
 * @returns {Promise<IDBDatabase>}
 */
const getDB = () => {
    return new Promise((resolve, reject) => {
        const request = indexedDB.open(DB_NAME);
        request.onerror = () => reject(request.error);
        request.onsuccess = () => resolve(request.result);
    });
};

/**
 * Fetch version metadata from server
 * @param {number} groupId - Group ID
 * @returns {Promise<Object>}
 */
export const fetchGroupVersionFromServer = async (groupId) => {
    try {
        const url = window.devMode 
            ? `http://localhost:80/api/db-group/${groupId}/metadata`
            : `/api/db-group/${groupId}/metadata`;
            
        const response = await fetch(url, {
            method: 'GET',
            headers: { 'Content-Type': 'application/json' }
        });
        
        const data = await response.json();
        
        if (data.status === 'success') {
            return {
                group_id: groupId,
                version: data.version,
                databases: data.databases || [],
                last_modified: data.last_modified,
                synced_at: Date.now()
            };
        }
        
        throw new Error(data.message || 'Failed to fetch version');
    } catch (error) {
        console.error('[Version] Error fetching group version:', error);
        throw error;
    }
};

/**
 * Get stored version from IndexedDB
 * @param {string} entityKey - Entity key (e.g., 'group_123')
 * @returns {Promise<Object|null>}
 */
export const getStoredVersion = async (entityKey) => {
    try {
        const db = await getDB();
        const transaction = db.transaction([STORES.VERSION_TRACKING], 'readonly');
        const store = transaction.objectStore(STORES.VERSION_TRACKING);
        
        return new Promise((resolve, reject) => {
            const request = store.get(entityKey);
            request.onsuccess = () => resolve(request.result || null);
            request.onerror = () => reject(request.error);
        });
    } catch (error) {
        console.error('[Version] Error getting stored version:', error);
        return null;
    }
};

/**
 * Store version in IndexedDB
 * @param {Object} versionData - Version data to store
 * @returns {Promise<boolean>}
 */
export const storeVersion = async (versionData) => {
    try {
        const db = await getDB();
        const transaction = db.transaction([STORES.VERSION_TRACKING], 'readwrite');
        const store = transaction.objectStore(STORES.VERSION_TRACKING);
        
        const versionEntry = {
            entity_key: versionData.entity_key,
            entity_type: versionData.entity_type,
            entity_id: versionData.entity_id,
            version: versionData.version,
            last_modified: versionData.last_modified,
            synced_at: Date.now()
        };
        
        return new Promise((resolve, reject) => {
            const request = store.put(versionEntry);
            request.onsuccess = () => {
                console.log(`[Version] Version stored for ${versionData.entity_key}`);
                resolve(true);
            };
            request.onerror = () => reject(request.error);
        });
    } catch (error) {
        console.error('[Version] Error storing version:', error);
        return false;
    }
};

/**
 * Check if version has changed
 * @param {number} groupId - Group ID
 * @returns {Promise<{changed: boolean, currentVersion: number, storedVersion: number}>}
 */
export const checkVersionChange = async (groupId) => {
    try {
        const entityKey = `group_${groupId}`;
        
        // Get stored version
        const stored = await getStoredVersion(entityKey);
        const storedVersion = stored ? stored.version : 0;
        
        // Fetch current version from server
        const current = await fetchGroupVersionFromServer(groupId);
        const currentVersion = current.version;
        
        // Store the new version
        await storeVersion({
            entity_key: entityKey,
            entity_type: 'group',
            entity_id: groupId,
            version: currentVersion,
            last_modified: current.last_modified
        });
        
        const changed = currentVersion > storedVersion;
        
        if (changed) {
            console.log(`[Version] Version changed for group ${groupId}: ${storedVersion} → ${currentVersion}`);
        } else {
            console.log(`[Version] Version unchanged for group ${groupId}: ${currentVersion}`);
        }
        
        return {
            changed,
            currentVersion,
            storedVersion
        };
    } catch (error) {
        console.error('[Version] Error checking version change:', error);
        return {
            changed: true, // Assume changed on error to trigger recalculation
            currentVersion: 0,
            storedVersion: 0
        };
    }
};

/**
 * Get current group version (from cache or server)
 * @param {number} groupId - Group ID
 * @param {boolean} forceRefresh - Force refresh from server
 * @returns {Promise<number>}
 */
export const getCurrentGroupVersion = async (groupId, forceRefresh = false) => {
    try {
        const entityKey = `group_${groupId}`;
        
        if (!forceRefresh) {
            // Try to get from cache first
            const stored = await getStoredVersion(entityKey);
            if (stored) {
                // Check if cache is recent (less than 1 minute old)
                const cacheAge = Date.now() - stored.synced_at;
                if (cacheAge < 60000) {
                    return stored.version;
                }
            }
        }
        
        // Fetch from server
        const current = await fetchGroupVersionFromServer(groupId);
        
        // Store the new version
        await storeVersion({
            entity_key: entityKey,
            entity_type: 'group',
            entity_id: groupId,
            version: current.version,
            last_modified: current.last_modified
        });
        
        return current.version;
    } catch (error) {
        console.error('[Version] Error getting current version:', error);
        return 0;
    }
};

/**
 * Compare versions
 * @param {number} version1 - First version
 * @param {number} version2 - Second version
 * @returns {number} -1 if v1 < v2, 0 if equal, 1 if v1 > v2
 */
export const compareVersions = (version1, version2) => {
    if (version1 < version2) return -1;
    if (version1 > version2) return 1;
    return 0;
};

/**
 * Get all stored versions
 * @returns {Promise<Array>}
 */
export const getAllStoredVersions = async () => {
    try {
        const db = await getDB();
        const transaction = db.transaction([STORES.VERSION_TRACKING], 'readonly');
        const store = transaction.objectStore(STORES.VERSION_TRACKING);
        
        return new Promise((resolve, reject) => {
            const request = store.getAll();
            request.onsuccess = () => resolve(request.result);
            request.onerror = () => reject(request.error);
        });
    } catch (error) {
        console.error('[Version] Error getting all versions:', error);
        return [];
    }
};

/**
 * Clear version tracking for an entity
 * @param {string} entityKey - Entity key
 * @returns {Promise<boolean>}
 */
export const clearEntityVersion = async (entityKey) => {
    try {
        const db = await getDB();
        const transaction = db.transaction([STORES.VERSION_TRACKING], 'readwrite');
        const store = transaction.objectStore(STORES.VERSION_TRACKING);
        
        return new Promise((resolve, reject) => {
            const request = store.delete(entityKey);
            request.onsuccess = () => {
                console.log(`[Version] Cleared version for ${entityKey}`);
                resolve(true);
            };
            request.onerror = () => reject(request.error);
        });
    } catch (error) {
        console.error('[Version] Error clearing entity version:', error);
        return false;
    }
};

/**
 * Clear all version tracking
 * @returns {Promise<boolean>}
 */
export const clearAllVersions = async () => {
    try {
        const db = await getDB();
        const transaction = db.transaction([STORES.VERSION_TRACKING], 'readwrite');
        const store = transaction.objectStore(STORES.VERSION_TRACKING);
        
        return new Promise((resolve, reject) => {
            const request = store.clear();
            request.onsuccess = () => {
                console.log('[Version] All versions cleared');
                resolve(true);
            };
            request.onerror = () => reject(request.error);
        });
    } catch (error) {
        console.error('[Version] Error clearing all versions:', error);
        return false;
    }
};
